/**
 * InvoiceAuditDesk
 * ----------------
 * Invoice Audit (Internal). Drop a supplier invoice and AskIsaac reads it like
 * a purchase order — amount due, which Sterile Mate PO it bills, the due date
 * and the bank instructions printed on it. Then it audits before money moves:
 * PO match, variance against what is left on the PO, duplicate invoice number,
 * and a loud warning when the bank details differ from what is on file.
 *
 * Board mode: full queue on the Sourcing Desk.
 * Scoped mode: pass linkType/linkId to show only one order's supplier invoices.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, BadgeCheck, Ban, Banknote, CheckCircle2, ClipboardList, Clock,
  FileText, Loader2, Mail, Plus, Receipt, Search, ShieldAlert, Upload, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import SupplierLedgerDialog from "@/components/admin/SupplierLedgerDialog";
import RecordPaymentDialog from "@/components/admin/RecordPaymentDialog";
import BatchPaymentDialog from "@/components/admin/BatchPaymentDialog";
import PaymentRunsPanel from "@/components/admin/PaymentRunsPanel";
import PoReconciliationPanel from "@/components/admin/PoReconciliationPanel";
import PayablesByPartyPanel from "@/components/admin/PayablesByPartyPanel";
import RemittanceEmailDialog from "@/components/admin/RemittanceEmailDialog";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATES = [
  { id: "needs_attention", label: "Needs attention" },
  { id: "awaiting_approval", label: "Awaiting approval" },
  { id: "due_soon", label: "Due & overdue" },
  { id: "open", label: "All open" },
  { id: "paid", label: "Paid" },
  { id: "all", label: "Everything" },
];

const Tile = ({ label, value, tone = "slate", testid }) => (
  <div className={`rounded-lg border px-3 py-2 ${tone === "red" ? "border-red-200 bg-red-50"
    : tone === "amber" ? "border-amber-200 bg-amber-50"
    : tone === "emerald" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}
    data-testid={testid}>
    <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-base font-black ${tone === "red" ? "text-red-700"
      : tone === "amber" ? "text-amber-800" : tone === "emerald" ? "text-emerald-700" : "text-slate-900"}`}>
      {value}
    </p>
  </div>
);

const emptyManual = {
  supplier_id: "", supplier_name: "", supplier_email: "", invoice_number: "",
  invoice_date: "", due_date: "", terms: "", amount: "", po_id: "", notes: "",
};

const PAY_METHODS = [
  ["check", "Check"], ["ach", "ACH"], ["wire", "Wire"], ["card", "Card"],
  ["cash", "Cash"], ["qbo_bill_pay", "QuickBooks Bill Pay"], ["other", "Other"],
];

const emptyPaid = {
  mark_paid: false, paid_method: "check", paid_kind: "full",
  paid_date: new Date().toISOString().slice(0, 10),
  paid_reference: "", paid_amount: "", paid_force: false,
};

/** Already-paid block shared by the upload review and the by-hand form. */
const MarkPaidFields = ({ value, onChange, amount, idPrefix }) => {
  const billed = Number(amount) || 0;
  const partial = value.paid_kind === "partial";
  const applied = partial ? (Number(value.paid_amount) || 0) : billed;
  const remaining = Math.max(0, billed - applied);
  return (
    <div className={`mt-2 rounded-lg border px-2.5 py-2 ${value.mark_paid
      ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200 bg-slate-50/60"}`}>
      <label className="flex items-start gap-2 text-[11.5px] font-bold text-slate-800">
        <input type="checkbox" checked={value.mark_paid} className="mt-0.5"
          onChange={(e) => onChange({ ...value, mark_paid: e.target.checked })}
          data-testid={`${idPrefix}-mark-paid`} />
        <span>
          This invoice is already paid — record the payment with it
          <span className="block font-normal text-[10.5px] text-slate-500">
            Posts the payment against this invoice and stamps it approved, so the ledger
            and the supplier statement agree. Leave it off to pay later from the queue.
          </span>
        </span>
      </label>
      {value.mark_paid && (
        <>
          <div className="flex flex-wrap gap-2 mt-2">
            {[["full", "Paid in full"], ["partial", "Partial payment"]].map(([k, label]) => (
              <button key={k} type="button"
                onClick={() => onChange({ ...value, paid_kind: k,
                  paid_amount: k === "full" ? "" : value.paid_amount })}
                className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${
                  value.paid_kind === k
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-600 border-slate-300"}`}
                data-testid={`${idPrefix}-paid-kind-${k}`}>{label}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-4 gap-2 mt-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Method
              <select value={value.paid_method}
                onChange={(e) => onChange({ ...value, paid_method: e.target.value })}
                className="h-8 w-full text-xs mt-0.5 border border-slate-300 rounded-md px-1.5 bg-white font-normal normal-case"
                data-testid={`${idPrefix}-paid-method`}>
                {PAY_METHODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </label>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Paid on
              <Input type="date" value={value.paid_date}
                onChange={(e) => onChange({ ...value, paid_date: e.target.value })}
                className="h-8 text-xs mt-0.5 font-normal normal-case"
                data-testid={`${idPrefix}-paid-date`} />
            </label>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Bank confirmation / check #
              <Input value={value.paid_reference}
                onChange={(e) => onChange({ ...value, paid_reference: e.target.value })}
                placeholder="Wire confirmation, ACH trace or check number"
                className="h-8 text-xs mt-0.5 font-normal normal-case"
                data-testid={`${idPrefix}-paid-reference`} />
            </label>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Amount paid
              <Input type="number" step="0.01" disabled={!partial}
                value={partial ? value.paid_amount : (billed ? billed.toFixed(2) : "")}
                placeholder={billed ? billed.toFixed(2) : "full amount"}
                onChange={(e) => onChange({ ...value, paid_amount: e.target.value })}
                className="h-8 text-xs mt-0.5 font-normal normal-case disabled:bg-slate-100"
                data-testid={`${idPrefix}-paid-amount`} />
            </label>
          </div>
          <p className="text-[10.5px] text-slate-600 mt-1.5" data-testid={`${idPrefix}-paid-summary`}>
            {partial
              ? `Recording ${money(applied)} of ${money(billed)} — ${money(remaining)} stays open on this invoice.`
              : `Recording the full ${money(billed)} — the invoice closes as paid.`}
            {!value.paid_reference && (
              <strong className="text-amber-700"> Add the bank confirmation number so the remittance can quote it.</strong>
            )}
          </p>
        </>
      )}
    </div>
  );
};

export const InvoiceAuditDesk = ({ token, linkType, linkId, compact = false, hideParties = false }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const fileRef = useRef(null);

  const [state, setState] = useState(compact ? "all" : "needs_attention");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parsing, setParsing] = useState(false);
  const [review, setReview] = useState(null);     // parse result being reviewed
  const [edit, setEdit] = useState({});           // editable proposal fields
  const [saveBank, setSaveBank] = useState(false);
  const [ack, setAck] = useState([]);
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState(emptyManual);
  const [pos, setPos] = useState([]);
  const [ledgerFor, setLedgerFor] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [picked, setPicked] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);
  const [remitOpen, setRemitOpen] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState("invoices");
  const [reviewPaid, setReviewPaid] = useState(emptyPaid);
  const [manualPaid, setManualPaid] = useState(emptyPaid);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { state, q: q || undefined };
      if (linkType && linkId) { params.link_type = linkType; params.link_id = linkId; }
      const r = await axios.get(`${API}/invoice-audit/queue`, { headers, params });
      setRows(r.data?.invoices || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the invoice audit queue");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, q, linkType, linkId, token]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const loadPos = useCallback(async (search) => {
    try {
      const params = { q: search || undefined };
      if (linkType && linkId) { params.link_type = linkType; params.link_id = linkId; }
      const r = await axios.get(`${API}/invoice-audit/pos`, { headers, params });
      setPos(r.data?.purchase_orders || []);
    } catch { /* picker stays empty */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkType, linkId, token]);

  useEffect(() => { loadPos(); }, [loadPos]);

  const upload = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (linkType && linkId) { fd.append("link_type", linkType); fd.append("link_id", linkId); }
      const r = await axios.post(`${API}/invoice-audit/parse`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setReview(d);
      setAck([]);
      setSaveBank(false);
      setEdit({
        supplier_id: d.supplier_match?.supplier_id || "",
        supplier_name: d.proposal.supplier_name || d.supplier_match?.name || "",
        supplier_email: d.proposal.supplier_email || d.supplier_match?.email || "",
        invoice_number: d.proposal.invoice_number || "",
        invoice_date: (d.proposal.invoice_date || "").slice(0, 10),
        due_date: (d.proposal.due_date || "").slice(0, 10),
        terms: d.proposal.terms || "",
        amount: d.proposal.amount ?? "",
        po_id: d.po_match?.po_id || "",
        notes: d.proposal.notes || "",
      });
      const high = (d.flags || []).filter((f) => f.severity === "high").length;
      toast[high ? "warning" : "success"](
        high ? `Read it — ${high} thing(s) to clear before approval` : "Invoice read and matched",
        { description: `${d.proposal.supplier_name || "Unknown supplier"} · ${money(d.proposal.amount)}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that invoice");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const paidPayload = (p) => (p.mark_paid ? {
    mark_paid: true,
    paid_method: p.paid_method,
    paid_date: p.paid_date || null,
    paid_reference: p.paid_reference || null,
    paid_amount: (p.paid_kind === "partial" && p.paid_amount !== "")
      ? Number(p.paid_amount) : null,
    paid_force: p.paid_force,
  } : { mark_paid: false });

  /** A partial with no figure in it would quietly pay the invoice in full. */
  const paidProblem = (p, billed) => {
    if (!p.mark_paid || p.paid_kind !== "partial") return null;
    const amt = Number(p.paid_amount);
    if (!p.paid_amount || !(amt > 0)) {
      return "Enter how much was actually paid, or switch back to Paid in full.";
    }
    if (Number(billed) && amt - Number(billed) > 0.009) {
      return `A partial payment cannot exceed the ${money(billed)} billed.`;
    }
    return null;
  };

  const announcePayment = (res, fallback) => {
    const pay = res?.payment;
    if (!pay) { toast.success(fallback); return; }
    if (pay.recorded) {
      toast.success(`${fallback} — payment recorded`, {
        description: `${money(pay.amount)} by ${(pay.method || "").replace(/_/g, " ")} · invoice now ${pay.invoice_status}` });
    } else {
      toast.warning(`${fallback}, but the payment was not recorded`, {
        duration: 11000,
        description: `${pay.error || "Record it from the queue."} Use the Pay button on the row to record it.` });
    }
  };

  const commit = async () => {
    const problem = paidProblem(reviewPaid, edit.amount);
    if (problem) { toast.error(problem); return; }
    setBusy(true);
    try {
      const body = {
        staging_id: review.staging_id,
        link_type: linkType || "standalone", link_id: linkId || "standalone",
        ...edit, amount: Number(edit.amount) || 0,
        po_id: edit.po_id || null,
        supplier_id: edit.supplier_id || null,
        save_bank_details: saveBank,
        acknowledged_flags: ack,
        ...paidPayload(reviewPaid),
      };
      const r = await axios.post(`${API}/invoice-audit/commit`, body, { headers });
      announcePayment(r.data, "Invoice posted to the payables ledger");
      setReview(null);
      setReviewPaid(emptyPaid);
      load();
      setRunKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post that invoice");
    } finally { setBusy(false); }
  };

  const saveManual = async () => {
    const problem = paidProblem(manualPaid, manual.amount);
    if (problem) { toast.error(problem); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/invoice-audit/manual`, {
        link_type: linkType || "standalone", link_id: linkId || "standalone",
        ...manual, amount: Number(manual.amount) || 0,
        po_id: manual.po_id || null, supplier_id: manual.supplier_id || null,
        acknowledged_flags: [],
        ...paidPayload(manualPaid),
      }, { headers });
      announcePayment(r.data, "Invoice recorded");
      setManual(emptyManual);
      setManualPaid(emptyPaid);
      setManualOpen(false);
      load();
      setRunKey((k) => k + 1);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not record that invoice";
      if (e?.response?.status === 409) {
        toast.warning(detail, { duration: 9000 });
      } else { toast.error(detail); }
    } finally { setBusy(false); }
  };

  const act = async (inv, action, extra = {}) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/invoice-audit/invoices/${inv.id}/audit`,
        { action, ...extra }, { headers });
      toast.success(
        action === "approve" ? "Approved for payment"
        : action === "dispute" ? "Marked disputed"
        : action === "void" ? "Voided"
        : action === "acknowledge" ? "Flag acknowledged" : "Updated",
        { description: action === "approve" ? `${r.data.invoice.invoice_number} · ${money(r.data.invoice.balance)}` : undefined });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not do that", { duration: 8000 });
    } finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/invoice-audit/alerts/send-digest`, {}, { headers });
      toast.success(r.data.sent ? `Digest emailed — ${r.data.overdue} overdue, ${r.data.due_soon} due soon`
                                : "Nothing due — no email sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the digest");
    } finally { setBusy(false); }
  };

  const bank = review?.bank_check;
  const profile = review?.supplier_match?.payment_profile;

  return (
    <section className={`rounded-xl border ${compact ? "border-sky-200 bg-sky-50/30" : "border-slate-200 bg-white"} p-4`}
      data-testid="invoice-audit-desk">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className={`font-black text-slate-900 inline-flex items-center gap-2 ${compact ? "text-xs uppercase tracking-wider" : "text-sm"}`}>
            <Receipt className="w-4 h-4 text-sky-600" />
            {compact ? "Supplier Invoice Audit (Internal)" : "Invoice Audit — Internal"}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {compact
              ? "What our suppliers billed us on this order — PO matched, approved, then paid."
              : "Upload a supplier invoice and AskIsaac reads the amount due, the Sterile Mate PO it bills, the due date and any bank details — then audits it before you pay."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" className="hidden" data-testid="invoice-audit-file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
            onChange={(e) => upload(e.target.files?.[0])} />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={parsing}
            className="bg-sky-700 hover:bg-sky-800" data-testid="invoice-audit-upload">
            {parsing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            {parsing ? "Reading…" : "Upload supplier invoice"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setManualOpen((v) => !v)}
            data-testid="invoice-audit-manual-open">
            <Plus className="w-3.5 h-3.5 mr-1" /> Enter by hand
          </Button>
          {!compact && (
            <Button size="sm" variant="outline" onClick={sendDigest} disabled={busy}
              data-testid="invoice-audit-digest">
              <Mail className="w-3.5 h-3.5 mr-1" /> Email due digest
            </Button>
          )}
        </div>
      </div>

      {!compact && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
          <Tile label="Open balance" value={money(summary.open_balance)} testid="tile-open" />
          <Tile label="Overdue" value={money(summary.overdue_balance)} tone="red" testid="tile-overdue" />
          <Tile label="Due in 7 days" value={money(summary.due_soon_balance)} tone="amber" testid="tile-due-soon" />
          <Tile label="Awaiting approval" value={summary.awaiting_approval} tone="amber" testid="tile-awaiting" />
          <Tile label="No PO linked" value={summary.unmatched_po} testid="tile-unmatched" />
          <Tile label="Flagged" value={summary.flagged} tone={summary.flagged ? "red" : "emerald"} testid="tile-flagged" />
        </div>
      )}

      {/* ---------------------------------------------------- parse review */}
      {review && (
        <div className="mt-4 rounded-lg border-2 border-sky-300 bg-white p-3" data-testid="invoice-audit-review">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-black uppercase tracking-wider text-sky-800">
              Review before it posts · confidence {review.proposal.confidence}
            </p>
            <button onClick={() => setReview(null)} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          {!!(review.flags || []).length && (
            <div className="mt-2 space-y-1.5">
              {review.flags.map((f) => {
                const done = ack.includes(f.code);
                return (
                  <div key={f.code + f.message.slice(0, 12)}
                    className={`rounded-md border px-2.5 py-2 text-[11px] flex items-start gap-2 ${
                      f.severity === "high" ? "border-red-300 bg-red-50 text-red-800"
                                            : "border-amber-200 bg-amber-50 text-amber-900"}`}
                    data-testid={`flag-${f.code}`}>
                    {f.severity === "high" ? <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                                           : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                    <span className="min-w-0">{f.message}</span>
                    <button
                      onClick={() => setAck((a) => done ? a.filter((x) => x !== f.code) : [...a, f.code])}
                      className={`ml-auto shrink-0 rounded px-2 py-0.5 text-[10px] font-bold uppercase border ${
                        done ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-current"}`}
                      data-testid={`ack-${f.code}`}>
                      {done ? "Acknowledged" : "Acknowledge"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {bank?.status === "changed" && (
            <div className="mt-2 rounded-md border-2 border-red-400 bg-red-50 p-2.5" data-testid="bank-change-warning">
              <p className="text-[11px] font-black uppercase tracking-wider text-red-800 inline-flex items-center gap-1.5">
                <ShieldAlert className="w-4 h-4" /> Bank details on this invoice differ from file
              </p>
              <table className="w-full text-[11px] mt-1.5">
                <thead><tr className="text-left text-slate-500">
                  <th className="font-bold">Field</th><th className="font-bold">On file</th><th className="font-bold">On this invoice</th>
                </tr></thead>
                <tbody>
                  {bank.diffs.map((d) => (
                    <tr key={d.field}>
                      <td className="py-0.5">{d.field.replace(/_/g, " ")}</td>
                      <td className="font-mono">{d.on_file}</td>
                      <td className="font-mono text-red-700 font-bold">{d.on_invoice}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10.5px] text-red-800 mt-1.5">
                Verify by phone with a known contact before paying. Do not use the number printed on the invoice.
              </p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-3">
            {[["supplier_name", "Supplier"], ["invoice_number", "Invoice #"],
              ["invoice_date", "Invoice date", "date"], ["due_date", "Due date", "date"],
              ["terms", "Terms"], ["amount", "Amount due", "number"],
              ["supplier_email", "Supplier email"]].map(([k, label, type]) => (
              <label key={k} className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {label}
                <Input type={type || "text"} value={edit[k] ?? ""}
                  onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}
                  className="h-8 text-xs mt-0.5 font-normal normal-case"
                  data-testid={`review-${k}`} />
              </label>
            ))}
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Billed against our PO
              <select value={edit.po_id || ""} onChange={(e) => setEdit({ ...edit, po_id: e.target.value })}
                className="h-8 w-full text-xs mt-0.5 border border-slate-300 rounded-md px-1.5 bg-white font-normal normal-case"
                data-testid="review-po">
                <option value="">— not linked —</option>
                {pos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.po_number} · {p.supplier_name} · {money(p.remaining)} left
                  </option>
                ))}
              </select>
            </label>
          </div>

          {review.po_match?.matched && (
            <p className="text-[11px] text-slate-600 mt-2" data-testid="po-match-line">
              Matched PO <strong>{review.po_match.po_number}</strong> · {money(review.po_match.po_amount)} issued ·
              {" "}{money(review.po_match.already_invoiced)} already invoiced ·
              {" "}<strong>{money(review.po_match.remaining_before_this)} remaining</strong>
            </p>
          )}

          {review.proposal.bank_details?.has_bank_details && (
            <label className="flex items-start gap-2 mt-2 text-[11px] text-slate-700">
              <input type="checkbox" checked={saveBank} className="mt-0.5"
                disabled={!edit.supplier_id}
                onChange={(e) => setSaveBank(e.target.checked)} data-testid="review-save-bank" />
              <span>
                Save the bank details on this invoice to {edit.supplier_name || "this supplier"}'s payment profile
                {!edit.supplier_id && <em className="text-slate-400"> — match a supplier on file first</em>}
                {profile?.has_bank_details && <strong className="text-amber-700"> (this will overwrite what is on file)</strong>}
              </span>
            </label>
          )}

          <Textarea rows={2} value={edit.notes || ""} className="mt-2 text-xs"
            onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
            placeholder="Audit notes — what you checked, who you spoke to" data-testid="review-notes" />

          <MarkPaidFields value={reviewPaid} onChange={setReviewPaid}
            amount={edit.amount} idPrefix="review" />

          <div className="flex justify-end gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => setReview(null)}>Discard</Button>
            <Button size="sm" onClick={commit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="invoice-audit-commit">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              Post to the ledger
            </Button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------ manual form */}
      {manualOpen && (
        <div className="mt-4 rounded-lg border border-slate-300 bg-slate-50 p-3" data-testid="invoice-audit-manual">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">
            Enter a supplier invoice by hand
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {[["supplier_name", "Supplier *"], ["invoice_number", "Invoice # *"],
              ["invoice_date", "Invoice date", "date"], ["due_date", "Due date", "date"],
              ["terms", "Terms"], ["amount", "Amount due *", "number"],
              ["supplier_email", "Supplier email"]].map(([k, label, type]) => (
              <label key={k} className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {label}
                <Input type={type || "text"} value={manual[k]}
                  onChange={(e) => setManual({ ...manual, [k]: e.target.value })}
                  className="h-8 text-xs mt-0.5 font-normal normal-case"
                  data-testid={`manual-${k}`} />
              </label>
            ))}
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Billed against our PO
              <select value={manual.po_id} onChange={(e) => setManual({ ...manual, po_id: e.target.value })}
                className="h-8 w-full text-xs mt-0.5 border border-slate-300 rounded-md px-1.5 bg-white font-normal normal-case"
                data-testid="manual-po">
                <option value="">— not linked —</option>
                {pos.map((p) => (
                  <option key={p.id} value={p.id}>{p.po_number} · {p.supplier_name} · {money(p.remaining)} left</option>
                ))}
              </select>
            </label>
          </div>
          <MarkPaidFields value={manualPaid} onChange={setManualPaid}
            amount={manual.amount} idPrefix="manual" />
          <div className="flex justify-end gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={saveManual} disabled={busy} className="bg-slate-900 hover:bg-slate-800"
              data-testid="manual-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Record invoice
            </Button>
          </div>
        </div>
      )}

      {!compact && !hideParties && (
        <div className="flex flex-wrap gap-2 mt-4 border-b border-slate-200 pb-0">
          {[["invoices", "Invoices"], ["parties", "Paid & outstanding by supplier"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`text-[11.5px] font-black px-3 py-1.5 -mb-px border-b-2 ${tab === id
                ? "border-sky-700 text-sky-800" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`audit-tab-${id}`}>{label}</button>
          ))}
        </div>
      )}

      {tab === "parties" && !compact && !hideParties ? (
        <div className="mt-3">
          <PayablesByPartyPanel token={token} refreshKey={runKey} />
        </div>
      ) : (
      <>
      {/* ----------------------------------------------------------- queue */}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {!compact && STATES.map((s) => (
          <button key={s.id} onClick={() => setState(s.id)}
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${state === s.id
              ? "bg-sky-700 text-white border-sky-700" : "bg-white text-slate-600 border-slate-200"}`}
            data-testid={`queue-state-${s.id}`}>{s.label}</button>
        ))}
        {!compact && (
          <div className="relative ml-auto">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pl-8 text-xs w-52"
              placeholder="Invoice #, supplier, PO" data-testid="queue-search" />
          </div>
        )}
      </div>

      {!!picked.length && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border-2 border-emerald-200 bg-emerald-50/60 p-2.5"
          data-testid="batch-select-bar">
          <span className="text-[12px] font-black text-emerald-900">
            {picked.length} invoice(s) selected ·{" "}
            {money(rows.filter((r) => picked.includes(r.id)).reduce((s, r) => s + (r.balance || 0), 0))} owed
          </span>
          {rows.some((r) => picked.includes(r.id) && r.balance > 0.01) && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7"
              onClick={() => setBatchOpen(true)} data-testid="batch-open-btn">
              <Banknote className="w-3.5 h-3.5 mr-1" /> Record batch payment
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7"
            onClick={() => setRemitOpen(true)} data-testid="remittance-open-btn">
            <Mail className="w-3.5 h-3.5 mr-1" /> Email the payment record
          </Button>
          <button className="text-[11px] font-bold text-slate-600 hover:underline"
            onClick={() => setPicked([])} data-testid="batch-clear-btn">Clear</button>
        </div>
      )}

      <div className="overflow-x-auto mt-2">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["", "Supplier / invoice", "Our PO", "Due", "Billed", "Balance", "Status", ""].map((h, i) => (
                <th key={`${h}-${i}`} className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className={r.overdue ? "bg-red-50/50" : r.due_soon ? "bg-amber-50/40" : ""}
                  data-testid={`invoice-row-${r.id}`}>
                  <td className="px-2 py-2">
                    {r.status !== "void" && (
                      <input type="checkbox" checked={picked.includes(r.id)}
                        data-testid={`select-${r.id}`}
                        onChange={(e) => setPicked(e.target.checked
                          ? [...picked, r.id] : picked.filter((x) => x !== r.id))} />
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <p className="font-bold text-slate-900 text-[12.5px]">{r.supplier_name}</p>
                    <p className="text-[11px] text-slate-500 font-mono">{r.invoice_number}</p>
                    {!!(r.open_flags || []).length && (
                      <p className="text-[10px] text-red-700 font-bold inline-flex items-center gap-1 mt-0.5">
                        <ShieldAlert className="w-3 h-3" /> {r.open_flags.length} open flag(s)
                      </p>
                    )}
                  </td>
                  <td className="px-2 py-2 text-[11px]">
                    {r.po_number || <span className="text-amber-700 font-bold">not linked</span>}
                    {r.po_rollup && (
                      <span className={`block text-[10px] font-semibold ${r.po_rollup.over_po
                        ? "text-red-700" : "text-slate-500"}`} data-testid={`po-rollup-${r.id}`}>
                        {r.po_rollup.invoice_count} inv · {money(r.po_rollup.invoiced)} of{" "}
                        {money(r.po_rollup.po_amount)}
                        {r.po_rollup.over_po ? ` · over by ${money(r.po_rollup.over_by)}`
                          : ` · ${money(r.po_rollup.remaining)} left`}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-[11px]">
                    {r.due_date || "—"}
                    {r.overdue && <span className="block text-red-700 font-bold">{r.days_past_due}d overdue</span>}
                    {r.due_soon && <span className="block text-amber-700 font-bold">in {r.days_to_due}d</span>}
                  </td>
                  <td className="px-2 py-2 text-[11px] font-semibold">{money(r.amount)}</td>
                  <td className="px-2 py-2 text-[11px] font-black">{money(r.balance)}</td>
                  <td className="px-2 py-2">
                    <span className="text-[9.5px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">
                      {(r.status || "").replace(/_/g, " ")}
                    </span>
                    {r.approved ? (
                      <span className="block text-[9.5px] text-emerald-700 font-bold mt-0.5 inline-flex items-center gap-1">
                        <BadgeCheck className="w-3 h-3" /> approved
                      </span>
                    ) : r.balance > 0.01 ? (
                      <span className="block text-[9.5px] text-amber-700 font-bold mt-0.5">needs approval</span>
                    ) : (
                      <span className="block text-[9.5px] text-slate-500 font-bold mt-0.5">paid without approval</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {!r.approved && r.status !== "void" && r.balance > 0.01 && (
                      <Button size="sm" variant="outline" className="h-7 mr-1" disabled={busy}
                        onClick={() => act(r, "approve")} data-testid={`approve-${r.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    )}
                    {r.balance > 0.01 && r.status !== "void" && (
                      <Button size="sm" className="h-7 mr-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => setPayFor(r)} data-testid={`pay-${r.id}`}>
                        <Banknote className="w-3.5 h-3.5 mr-1" /> Pay
                      </Button>
                    )}
                    <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className="text-[11px] font-bold text-sky-700 hover:underline mr-2"
                      data-testid={`expand-${r.id}`}>
                      {expanded === r.id ? "Hide" : "Detail"}
                    </button>
                    {r.supplier_id && (
                      <button onClick={() => setLedgerFor({ id: r.supplier_id, name: r.supplier_name })}
                        className="text-[11px] font-bold text-slate-600 hover:underline"
                        data-testid={`ledger-${r.id}`}>Ledger</button>
                    )}
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-slate-50/70">
                    <td colSpan={8} className="px-3 py-2">
                      <div className="grid sm:grid-cols-2 gap-3 text-[11px]">
                        <div>
                          <p className="font-black uppercase tracking-wider text-slate-500 text-[9.5px] mb-1">Audit trail</p>
                          {(r.audit_log || []).map((a, i) => (
                            <p key={i} className="text-slate-600">
                              <strong>{a.action}</strong> · {a.by} · {a.at ? new Date(a.at).toLocaleString() : ""}
                              {a.note ? ` — ${a.note}` : ""}
                            </p>
                          ))}
                          {r.approved && (
                            <p className="text-emerald-700 font-bold mt-1">
                              Approved by {r.approved_by} on {new Date(r.approved_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="font-black uppercase tracking-wider text-slate-500 text-[9.5px] mb-1">Open flags</p>
                          {(r.open_flags || []).length ? r.open_flags.map((f) => (
                            <div key={f.code} className="flex items-start gap-2 mb-1">
                              <span className="text-red-700">{f.message}</span>
                              <button onClick={() => act(r, "acknowledge", { flag_code: f.code, note: "Acknowledged from the queue" })}
                                className="ml-auto text-[10px] font-bold uppercase border border-slate-300 rounded px-1.5 py-0.5 bg-white"
                                data-testid={`row-ack-${r.id}-${f.code}`}>Acknowledge</button>
                            </div>
                          )) : <p className="text-slate-500">None.</p>}
                          <div className="flex gap-2 mt-2">
                            {r.approved && (
                              <Button size="sm" variant="outline" className="h-7" disabled={busy}
                                onClick={() => act(r, "unapprove", { note: "Approval withdrawn" })}
                                data-testid={`unapprove-${r.id}`}>Withdraw approval</Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-amber-700" disabled={busy}
                              onClick={() => act(r, "dispute", { note: "Disputed from the audit queue" })}
                              data-testid={`dispute-${r.id}`}>
                              <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Dispute
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-red-700" disabled={busy}
                              onClick={() => act(r, "void", { note: "Voided from the audit queue" })}
                              data-testid={`void-${r.id}`}>
                              <Ban className="w-3.5 h-3.5 mr-1" /> Void
                            </Button>
                            {!!(r.attachments || []).length && (
                              <span className="text-[10.5px] text-slate-500 inline-flex items-center gap-1 ml-1">
                                <FileText className="w-3.5 h-3.5" /> {r.attachments.length} file(s) attached
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={8} className="px-2 py-5 text-center text-sm text-slate-400">
                {compact ? "No supplier invoices recorded on this order yet."
                         : "Nothing in this view. Upload a supplier invoice to start the audit."}
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="px-2 py-5 text-center text-sm text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {!compact && summary && (
        <div className="mt-3 flex flex-wrap gap-2 text-[10.5px]">
          <span className="font-black uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Aging
          </span>
          {[["current", "Current"], ["1_30", "1-30"], ["31_60", "31-60"], ["61_90", "61-90"],
            ["over_90", "90+"], ["no_due_date", "No due date"]].map(([k, label]) => (
            <span key={k} className="px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">
              {label}: <strong>{money(summary.aging[k])}</strong>
            </span>
          ))}
        </div>
      )}

      </>
      )}

      <SupplierLedgerDialog open={!!ledgerFor} onClose={() => setLedgerFor(null)}
        supplierId={ledgerFor?.id} supplierName={ledgerFor?.name} token={token} />

      <RecordPaymentDialog invoice={payFor} token={token} onClose={() => setPayFor(null)}
        onSaved={() => { load(); setRunKey((k) => k + 1); }} />

      <RemittanceEmailDialog open={remitOpen} invoiceIds={picked} token={token}
        onClose={() => setRemitOpen(false)}
        onSent={() => { setPicked([]); load(); }} />

      {batchOpen && (
        <BatchPaymentDialog token={token}
          invoices={rows.filter((r) => picked.includes(r.id))}
          onClose={() => setBatchOpen(false)}
          onSaved={() => { setPicked([]); load(); setRunKey((k) => k + 1); }} />
      )}

      {!compact && !hideParties && (
        <div className="mt-5 space-y-5">
          <PaymentRunsPanel token={token} refreshKey={runKey} />
          <PoReconciliationPanel token={token} refreshKey={runKey} />
        </div>
      )}
    </section>
  );
};

export default InvoiceAuditDesk;
